import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { ArrowLeft, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LIKERT = [
  { value: 1, label: 'Rarely true' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'About half' },
  { value: 4, label: 'Often' },
  { value: 5, label: 'Consistently' },
];

export default function AssessmentFlow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [phase, setPhase] = useState('loading'); // loading | identity | running | submitting | error
  const [errMsg, setErrMsg] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [needsIdentity, setNeedsIdentity] = useState(false);

  // Bootstrap requests belong to this token only; discard responses after cleanup.
  useEffect(() => {
    document.title = 'Apex Six Athlete Self-Assessment';
    setPhase('loading');
    setErrMsg('');
    setQuestions([]);
    setAnswers({});
    setIdx(0);
    setName('');
    setEmail('');
    setNeedsIdentity(false);
    if (!token) {
      setPhase('error');
      setErrMsg('Missing access token. Open the assessment from your purchase confirmation link.');
      return;
    }
    const controller = new AbortController();
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/assessment/questions?token=${encodeURIComponent(token)}`, {
          signal: controller.signal,
        });
        if (!active) return;
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          if (!active) return;
          setPhase('error');
          setErrMsg(b.detail || `Could not load assessment (${r.status}).`);
          return;
        }
        const d = await r.json();
        if (!active) return;
        if (d.already_submitted) {
          navigate(`/assessment/results/${token}`, { replace: true });
          return;
        }
        setQuestions(d.questions || []);
        if (d.email) setEmail(d.email);
        if (d.name) setName(d.name);
        const missingIdentity = !d.email || !d.name;
        setNeedsIdentity(missingIdentity);
        setPhase(missingIdentity ? 'identity' : 'running');
      } catch {
        if (!active || controller.signal.aborted) return;
        setPhase('error');
        setErrMsg('Network error. Please refresh.');
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [token, navigate]);

  const currentQ = questions[idx];
  const answeredCount = Object.keys(answers).length;
  const allAnswered = questions.length > 0 && answeredCount === questions.length;
  const progressPct = useMemo(
    () => (questions.length ? Math.round(((idx + (answers[currentQ?.id] ? 1 : 0)) / questions.length) * 100) : 0),
    [idx, answers, questions.length, currentQ],
  );

  const pickAnswer = (val) => {
    if (!currentQ) return;
    setAnswers((prev) => ({ ...prev, [currentQ.id]: val }));
    // auto-advance with a tiny delay for tactile feel
    window.setTimeout(() => {
      setIdx((i) => Math.min(i + 1, questions.length - 1));
    }, 160);
  };

  const submit = async () => {
    if (!allAnswered) {
      toast.error('Please answer every question first.');
      return;
    }
    setPhase('submitting');
    try {
      const ordered = questions.map((q) => answers[q.id]);
      const r = await fetch(`${BACKEND}/api/assessment/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          result_token: token,
          email: email.trim() || undefined,
          name: name.trim() || undefined,
          answers: ordered,
        }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setPhase('running');
        toast.error(b.detail || 'Could not submit. Please try again.');
        return;
      }
      const d = await r.json();
      navigate(d.redirect || `/assessment/results/${token}`, { replace: true });
    } catch {
      setPhase('running');
      toast.error('Network error. Try again.');
    }
  };

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center" data-testid="flow-loading">
        <Loader2 className="h-8 w-8 text-[#004C54] animate-spin" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-5" data-testid="flow-error">
        <div className="max-w-md w-full bg-white border border-[#E5E7E7] rounded-2xl p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="font-heading text-xl font-bold tracking-tight mt-4">We can&rsquo;t open this assessment.</h1>
          <p className="text-sm text-foreground/65 mt-2">{errMsg}</p>
          <a href="/assessment" className="inline-block mt-5 text-[#004C54] underline underline-offset-4">Back to /assessment</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0a0d0e]" data-testid="assessment-flow">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-[#004C54] text-white flex items-center justify-center font-bold">A</div>
            <span className="font-semibold tracking-tight text-sm">Apex Six · Athlete Self-Assessment</span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-mono font-bold text-foreground/55">
            {phase === 'identity' ? 'About you' : `${idx + 1} / ${questions.length}`}
          </div>
        </div>
        {phase !== 'identity' && (
          <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-4">
            <Progress value={progressPct} className="h-1.5" data-testid="flow-progress" />
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        {phase === 'identity' && (
          <div className="max-w-lg mx-auto" data-testid="flow-identity">
            <div className="text-[11px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">Before we start</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">First, tell us about the athlete.</h1>
            <p className="mt-3 text-foreground/65">This assessment is built for the athlete to take themselves. We&rsquo;ll personalize your IDP and email it to you when you&rsquo;re done.</p>
            <div className="mt-6 space-y-3">
              <div>
                <Label htmlFor="flow-name" className="text-xs font-semibold text-foreground/70">Athlete&rsquo;s first name</Label>
                <Input id="flow-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex" className="bg-white mt-1.5" data-testid="flow-name-input" />
              </div>
              <div>
                <Label htmlFor="flow-email" className="text-xs font-semibold text-foreground/70">Email (athlete or parent — for IDP delivery)</Label>
                <Input id="flow-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@example.com" className="bg-white mt-1.5" data-testid="flow-email-input" />
              </div>
            </div>
            <Button
              size="lg"
              className="mt-6 w-full h-12 rounded-full bg-[#004C54] hover:bg-[#003a40]"
              disabled={!name.trim() || !email.trim()}
              onClick={() => setPhase('running')}
              data-testid="flow-identity-continue"
            >
              Start the assessment <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {phase === 'running' && currentQ && (
          <div data-testid="flow-question">
            <div className="text-[11px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">{currentQ.dimension}</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3 leading-tight" data-testid="flow-prompt">{currentQ.prompt}</h2>
            <p className="mt-2 text-sm text-foreground/55">Rate how true this is for you on a typical day.</p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-5 gap-2">
              {LIKERT.map((opt) => {
                const selected = answers[currentQ.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => pickAnswer(opt.value)}
                    className={`group rounded-xl border px-4 py-4 text-left sm:text-center transition-colors ${
                      selected
                        ? 'border-[#004C54] bg-[#004C54] text-white'
                        : 'border-[#E5E7E7] bg-white hover:border-[#004C54]/40'
                    }`}
                    data-testid={`flow-option-${currentQ.id}-${opt.value}`}
                  >
                    <div className={`text-2xl font-bold tracking-tight ${selected ? 'text-white' : 'text-[#004C54]'}`}>{opt.value}</div>
                    <div className={`text-[11px] mt-1 ${selected ? 'text-white/85' : 'text-foreground/55'}`}>{opt.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                data-testid="flow-back"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {idx < questions.length - 1 ? (
                <Button
                  onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))}
                  disabled={!answers[currentQ.id]}
                  className="bg-[#0a0d0e] hover:bg-black"
                  data-testid="flow-next"
                >
                  Next <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!allAnswered}
                  className="bg-[#004C54] hover:bg-[#003a40]"
                  data-testid="flow-submit"
                >
                  See my IDP <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {phase === 'submitting' && (
          <div className="text-center py-20" data-testid="flow-submitting">
            <Loader2 className="h-10 w-10 text-[#004C54] animate-spin mx-auto" />
            <h2 className="font-heading text-xl font-bold tracking-tight mt-4">Scoring your assessment…</h2>
            <p className="text-sm text-foreground/65 mt-2">Building your IDP, generating your discount code, and emailing your PDF.</p>
          </div>
        )}
      </main>
    </div>
  );
}
